import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import {
  Target,
  Upload as UploadIcon,
  Trash2,
  Loader2,
  Search,
  AlertTriangle,
  CheckCircle2,
  Wand2,
  Link2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/api/client";

/**
 * /admin/distribution-scope — admin view of the Efficiency report's employee
 * scope. Lists every employee currently in scope, lets the user clear it or
 * jump to the upload page to replace it.
 */
export default function AdminDistributionScope() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({
    total_count: 0,
    last_updated_at: null,
    source_filename: null,
  });
  const [entries, setEntries] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(true);
  const [assigning, setAssigning] = useState(null); // scope_name_norm currently being assigned
  const [selectedCanonical, setSelectedCanonical] = useState({}); // {scope_norm: canonical_id}
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/distribution-scope");
      setEntries(data.entries || []);
      setMeta({
        total_count: data.total_count || 0,
        last_updated_at: data.last_updated_at,
        source_filename: data.source_filename,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnmatched = useCallback(async () => {
    setUnmatchedLoading(true);
    try {
      const { data } = await api.get("/distribution-scope/unmatched");
      setUnmatched(data.entries || []);
      // Default-select the top suggestion (>= 0.4) for every entry so the
      // "Aplicar sugerencia" button has something to send right away.
      const preselect = {};
      (data.entries || []).forEach((e) => {
        const top = e.suggestions?.[0];
        if (top && top.confidence >= 0.4) {
          preselect[e.employee_name_norm] = top.canonical_id;
        }
      });
      setSelectedCanonical((prev) => ({ ...preselect, ...prev }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUnmatchedLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadUnmatched();
  }, [load, loadUnmatched]);

  const onAssign = async (scope_name_norm) => {
    const canonical_id = selectedCanonical[scope_name_norm];
    if (!canonical_id) {
      toast.error(t("scope.assign.selectFirst"));
      return;
    }
    setAssigning(scope_name_norm);
    try {
      await api.post("/distribution-scope/assign", {
        scope_name_norm,
        canonical_id,
      });
      toast.success(t("scope.assign.done"));
      // Refresh both lists — assignment changes both.
      await Promise.all([load(), loadUnmatched()]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAssigning(null);
    }
  };

  const onClear = async () => {
    setClearing(true);
    try {
      await api.delete("/distribution-scope");
      toast.success(t("scope.cleared"));
      setConfirmClear(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearing(false);
    }
  };

  const filtered = entries.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      e.employee_name.toLowerCase().includes(q) ||
      e.employee_id_raw.toLowerCase().includes(q) ||
      (e.canonical_name || "").toLowerCase().includes(q)
    );
  });

  const updatedLabel = meta.last_updated_at
    ? new Date(meta.last_updated_at).toLocaleString(
        i18n.language === "en" ? "en-US" : "es-MX",
      )
    : null;
  const isEmpty = meta.total_count === 0;
  const noCanonicalCount = entries.filter((e) => !e.has_canonical).length;

  return (
    <div className="space-y-6" data-testid="admin-distribution-scope-page">
      <Toaster richColors position="top-right" />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <Target className="h-7 w-7 text-emerald-500" />
            {t("scope.adminTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("scope.adminSubtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/upload")}
            data-testid="scope-admin-upload-btn"
          >
            <UploadIcon className="mr-2 h-4 w-4" />
            {t("scope.uploadCta")}
          </Button>
          {!isEmpty && (
            <Button
              variant="outline"
              size="sm"
              className="border-rose-500/40 text-rose-700 hover:bg-rose-100/60 dark:text-rose-300 dark:hover:bg-rose-900/40"
              onClick={() => setConfirmClear(true)}
              data-testid="scope-admin-clear-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("scope.clearScope")}
            </Button>
          )}
        </div>
      </div>

      {isEmpty ? (
        <Alert className="border-amber-500/40 bg-amber-50 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-300">
            {t("scope.bannerEmptyTitle")}
          </AlertTitle>
          <AlertDescription className="text-amber-700/80 dark:text-amber-200/80">
            {t("scope.noActiveScope")}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <Card data-testid="scope-kpi-count">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("scope.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tabular-nums text-emerald-600">
                {meta.total_count}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("upload.recent.uploaded_at")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">
                {updatedLabel || "—"}
              </div>
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {meta.source_filename || t("scope.listSubtitle")}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("scope.noCanonical")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tabular-nums text-amber-600">
                {noCanonicalCount}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empleados sin canónico: resolver para que entren a Eficiencia */}
      {unmatched.length > 0 && (
        <Card
          className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20"
          data-testid="scope-unmatched-panel"
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {t("scope.unmatchedTitle", { n: unmatched.length })}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {t("scope.unmatchedSubtitle")}
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {unmatchedLoading ? (
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              unmatched.map((u) => {
                const top = u.suggestions?.[0];
                const hasGoodTop = top && top.confidence >= 0.5;
                return (
                  <div
                    key={u.employee_name_norm}
                    className="flex flex-col gap-2 rounded border border-amber-500/30 bg-card p-3 sm:flex-row sm:items-center"
                    data-testid={`unmatched-row-${u.employee_name_norm}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{u.employee_name}</div>
                      <div className="text-xs text-muted-foreground">
                        #{u.employee_id_raw || "—"}
                        {hasGoodTop && (
                          <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                            <Wand2 className="h-3 w-3" />
                            {t("scope.assign.bestMatch", {
                              name: top.canonical_name,
                              pct: Math.round(top.confidence * 100),
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={selectedCanonical[u.employee_name_norm] || ""}
                        onValueChange={(v) =>
                          setSelectedCanonical((p) => ({ ...p, [u.employee_name_norm]: v }))
                        }
                      >
                        <SelectTrigger
                          className="h-8 w-64 text-xs"
                          data-testid={`unmatched-select-${u.employee_name_norm}`}
                        >
                          <SelectValue placeholder={t("scope.assign.pick")} />
                        </SelectTrigger>
                        <SelectContent>
                          {u.suggestions.map((s) => (
                            <SelectItem key={s.canonical_id} value={s.canonical_id}>
                              {s.canonical_name}
                              <span className="ml-2 text-[10px] text-muted-foreground">
                                {Math.round(s.confidence * 100)}%
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAssign(u.employee_name_norm)}
                        disabled={
                          assigning === u.employee_name_norm ||
                          !selectedCanonical[u.employee_name_norm]
                        }
                        data-testid={`unmatched-assign-${u.employee_name_norm}`}
                      >
                        {assigning === u.employee_name_norm ? (
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Link2 className="mr-2 h-3.5 w-3.5" />
                        )}
                        {t("scope.assign.button")}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("common.search")}
          className="pl-9"
          data-testid="scope-admin-search"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead className="hidden sm:table-cell">
                  {t("employeesCanonical.aliases") || "Código"}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  Canonical
                </TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-sm text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-sm text-muted-foreground">
                    {isEmpty ? t("scope.noActiveScope") : t("common.no_data")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((e) => (
                  <TableRow key={e.employee_name_norm} data-testid={`scope-row-${e.employee_name_norm}`}>
                    <TableCell className="font-medium">{e.employee_name}</TableCell>
                    <TableCell className="hidden font-mono text-xs sm:table-cell">
                      {e.employee_id_raw || "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {e.has_canonical ? (
                        <Link
                          to="/admin/employees"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          {e.canonical_name}
                        </Link>
                      ) : (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-300">
                          {t("scope.noCanonical")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
                      >
                        {t("scope.matched") || "In scope"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="max-w-md" data-testid="scope-admin-clear-dialog">
          <DialogHeader>
            <DialogTitle>{t("scope.clearTitle")}</DialogTitle>
            <DialogDescription>{t("scope.clearBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmClear(false)}
              disabled={clearing}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={onClear}
              disabled={clearing}
              data-testid="scope-admin-clear-confirm"
            >
              {clearing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t("scope.clearScope")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
